import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { filter, skip, takeUntil } from 'rxjs/operators';
import { ACCESS_SUSPENDED_PAGE } from '@pages/access-suspended/access-suspended.constants';
import { COPILOT_ENTRY_AUTHORITIES } from '@pages/copilot/copilot.constants';
import { BaseObject } from '@shared/base/base-object';
import { Authorities } from '@shared/constants/authorities';
import { getElementRect, isRectInsideOtherRect } from '@shared/helpers/coordinate.helper';
import { UserpilotService } from '@shared/helpers/userpilot.service';
import { LogoState } from '@shared/states/logo.state';
import { SettingsState } from '@shared/states/settings.state';
import { UserState } from '@shared/states/user.state';
import { TranslateService } from '@shared/translates/translate.service';
import { Authority } from '@shared/types/authority';
import { Rect } from '@shared/types/rect';
import { getDialogPosition } from '@ui/dialog/dialog-position';
import { LayoutState } from 'app/layout/layout.state';
import { NotificationsService } from 'app/notifications/notifications.service';
import { ProfileMenuComponent } from '../profile-menu/profile-menu.component';
import { NavbarState } from './navbar.state';
import { NavbarItem } from './navbar.types';

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrls: ['./navbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'bg-main',
  },
  standalone: false,
})
export class NavbarComponent extends BaseObject implements OnInit, AfterViewChecked {
  public showMenu: boolean;
  public navbarItems$ = new BehaviorSubject<NavbarItem[]>([]);
  public popupNavbarItems$ = new BehaviorSubject<NavbarItem[]>([]);
  private filteredNavbarItems$ = new BehaviorSubject<NavbarItem[]>([]);
  private calculatedRectsNavItems: Rect[];

  @ViewChild('nav', { static: true, read: ElementRef }) public navElement: ElementRef<HTMLElement>;

  @ViewChild('navItemTester', { static: true })
  public navItemTesterElement: ElementRef<HTMLElement>;

  constructor(
    public layoutState: LayoutState,
    public logoState: LogoState,
    public notificationsService: NotificationsService,
    public userState: UserState,
    public userpilotService: UserpilotService,
    public settingsState: SettingsState,
    private cd: ChangeDetectorRef,
    private dialog: MatDialog,
    private router: Router,
    private translateService: TranslateService,
    private viewContainerRef: ViewContainerRef,
    private navbarState: NavbarState,
  ) {
    super();
  }

  /**
   * While access is suspended the suspension guard rejects every other route, so navigation is
   * dimmed and made unclickable rather than silently bouncing back. Also keyed off the route itself,
   * so the suspended screen always reads as locked regardless of the state that got us there.
   * The profile menu stays live — logging out is the one action that must remain available.
   */
  public get isNavigationBlocked(): boolean {
    return this.userState.isAccessSuspended || this.isOnAccessSuspendedPage;
  }

  private get isOnAccessSuspendedPage(): boolean {
    return this.router.url.split(/[?#]/)[0] === ACCESS_SUSPENDED_PAGE;
  }

  public ngOnInit(): void {
    this.listenToWindowResize();
    this.listenToTranslationChange();

    this.initNavbarItems();

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.cd.markForCheck());

    this.notificationsService.getUnreadPushNotificationsCount();
  }

  public ngAfterViewChecked(): void {
    this.recalculate();
  }

  private initNavbarItems(): void {
    const allNavbarItems = this.getAllNavbarItems();

    this.filteredNavbarItems$.next(
      allNavbarItems.filter((menuItem) => {
        const _hasAuthorities = this.userState.hasAuthorities(menuItem.authorities, 'one_of');

        return (
          _hasAuthorities ||
          (this.userState.needSubscription() &&
            this.userState.hasDisabledAuthorities(menuItem.authorities, 'one_of') &&
            !this.userState.hasEarlyAccess(menuItem.authorities, 'one_of'))
        );
      }),
    );

    this.calculatedRectsNavItems = this.filteredNavbarItems$.value.map((menuItem) =>
      this.getTextRect(this.translateService.get(menuItem.name)),
    );
  }

  private getAllNavbarItems(): NavbarItem[] {
    const navItems: NavbarItem[] = [
      {
        name: 'pages.dashboard.name',
        url: '/dashboard',
      },

      !this.userState.isClient
        ? {
            name: 'pages.portfolios.name',
            url: '/portfolios',
            authorities: [Authorities.PortfoliosView],
          }
        : null,

      {
        name: 'pages.positions.name',
        url: '/positions',
        authorities: [Authorities.PositionsView],
      },

      !this.userState.isClient
        ? {
            name: 'pages.products.name',
            url: '/products',
            authorities: [
              Authorities.StrategiesView,
              Authorities.RecommendationView,
              Authorities.GlobalAllocationView,
            ],
          }
        : null,

      {
        name: 'pages.transactions.name',
        url: '/transactions',
        authorities: [Authorities.TransactionsView],
      },

      !this.userState.isClient
        ? {
            name: 'pages.instruments.name',
            url: '/instruments',
            authorities: [Authorities.InstrumentsView],
          }
        : null,

      // NO CONTOUR LIST (was `isCopilotEnabledForSite(siteName)`): visibility is
      // decided by initNavbarItems' generic entitlement filter below — holds one
      // of the authorities, or the sellable-module upsell path — exactly like
      // every other purchasable module. The hardcoded SiteName allow-list meant a
      // new tenant needed a webapp release before Copilot appeared at all.
      {
        name: 'pages.copilot.name',
        url: '/copilot',
        // E8 (review #5): the SAME derived one_of set the /copilot route uses
        // (COPILOT_ENTRY_AUTHORITIES = the union of the COPILOT_TABS
        // authorities incl. the write/manage surfaces). This list used to be
        // hand-maintained and NARROWER than both the tabs and the route
        // (RECON_VIEW + ALERT_VIEW only), so a REPORT_VIEW-only / ASK-only /
        // CIO_VIEW-only user never saw the menu item for a page that is
        // theirs. Do NOT inline a literal list here again — it drifts.
        //
        // S3b (review #5.2): the FROZEN union object itself, not a
        // `[...spread]` copy — a copy is mutable at runtime (`readonly` is
        // erased), and emptying it does not hide the item, it shows it to
        // EVERYONE (hasAuthorities([]) === TRUE). The `as Authority[]` cast is
        // only needed because NavbarItem.authorities predates `readonly`;
        // nothing mutates it (the three call sites in initNavbarItems only
        // READ it), and the freeze makes any future write a TypeError.
        authorities: COPILOT_ENTRY_AUTHORITIES as Authority[],
        badge: { text: 'pages.copilot.badge', color: 'success' as const },
      },

      !this.userState.isClient
        ? {
            name: 'pages.screener.name',
            url: '/screener',
            authorities: [Authorities.ModelPortfoliosView],
          }
        : null,

      !this.userState.isClient
        ? {
            name: 'pages.risks.name',
            url: '/risks',
            authorities: [
              Authorities.MandateRiskProfileView,
              Authorities.RestrictionAlertView,
              Authorities.RiskReportView,
            ],
          }
        : null,
      {
        name: 'marketplace.name',
        url: '/marketplace',
        authorities: [Authorities.MarketplaceView],
      },
      {
        name: 'pages.accounts.name',
        url: '/accounts',
        authorities: this.userState.isClient
          ? [Authorities.AccountsView, Authorities.BankAccountsView]
          : [
              Authorities.AccountsView,
              Authorities.BankAccountsView,
              Authorities.ClientsView,
              Authorities.MandatesView,
            ],
      },

      !this.userState.isClient
        ? {
            name: 'pages.reports.name',
            url: '/reports',
            authorities: [Authorities.ReportsView],
          }
        : null,

      !this.userState.isClient
        ? {
            name: 'common.import',
            url: '/import',
            authorities: [
              Authorities.ManualImportPositionsView,
              Authorities.ManualImportTransactionsView,
              Authorities.TransactionLoaderView,
            ],
          }
        : null,

      !this.userState.isClient
        ? {
            name: 'common.reconciliation',
            url: '/reconciliation',
            authorities: [Authorities.BankStatsView],
          }
        : null,

      !this.userState.isClient
        ? {
            name: 'pages.retail.name',
            url: '/retail',
            authorities: [Authorities.RetailAdminPanelView],
          }
        : null,
    ];

    return navItems.filter((item) => item);
  }

  public changeActiveNavItem(event: boolean, menuItem: NavbarItem): void {
    if (event) {
      this.navbarState.currentMenuItem$.next(menuItem);
    }
  }

  private listenToWindowResize(): void {
    fromEvent(window, 'resize')
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.recalculate());
  }

  private listenToTranslationChange(): void {
    this.translateService.currentLanguage$
      .pipe(skip(1), takeUntil(this.destroy$))
      .subscribe(() => this.recalculate());
  }

  private getTextRect(testText: string): Rect {
    this.navItemTesterElement.nativeElement.textContent = testText;

    return getElementRect(this.navItemTesterElement.nativeElement);
  }

  private recalculate(): void {
    this.calculatedRectsNavItems = this.filteredNavbarItems$.value.map((menuItem) =>
      this.getTextRect(this.translateService.get(menuItem.name)),
    );

    const navRect = getElementRect(this.navElement.nativeElement);
    navRect.left += 100;
    navRect.width -= 100;

    let widthSum = 0;

    const visibleNavbarItem: NavbarItem[] = [];
    const invisibleNavbarItem: NavbarItem[] = [];

    for (let index = 0; index < this.calculatedRectsNavItems.length; index++) {
      const navItemRect = this.calculatedRectsNavItems[index];

      const navItemTestRect: Rect = {
        left: navRect.left + widthSum,
        right: navRect.left + widthSum + navItemRect.width,
        top: navRect.top,
        bottom: navRect.bottom,
        width: navItemRect.width,
        height: navItemRect.height,
      };

      if (isRectInsideOtherRect(navItemTestRect, navRect)) {
        visibleNavbarItem.push(this.filteredNavbarItems$.value[index]);
      } else {
        invisibleNavbarItem.push(this.filteredNavbarItems$.value[index]);
      }

      widthSum += navItemRect.width;
    }

    this.navbarItems$.next(visibleNavbarItem);
    this.popupNavbarItems$.next(invisibleNavbarItem);

    this.cd.detectChanges();
  }

  public _onShowNotificationsClick(): void {
    this.notificationsService.openSystemNotificationsDialog();
  }

  public _onToMainPageClick(): void {
    this.router.navigate(['/']);
  }

  public _onProfileMenuClick(element: HTMLElement): void {
    this.dialog.open<ProfileMenuComponent, void, void>(ProfileMenuComponent, {
      width: '280px',
      height: 'auto',
      position: getDialogPosition(element, 'right'),
      viewContainerRef: this.viewContainerRef,
      autoFocus: false,
    });
  }
}
